import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  CheckCircle2, 
  ArrowRight,
  Star,
  Phone,
  MapPin,
  Mail,
  Target,
  Eye,
  Heart
} from 'lucide-react';
import Button from '../../components/Button';
import MobileMenu from '../../components/MobileMenu';
import { useSiteConfig } from '../../context/SiteCustomizationContext'; // Changed import

import PublicLayout from '../../components/PublicLayout';

const AproposPage: React.FC = () => {
  const { allConfig, loading } = useSiteConfig();

  useEffect(() => {
    if (allConfig) {
      document.title = `${allConfig?.about?.title || 'À propos'} | ${allConfig?.identity?.siteName || 'Elecborn'}`;
    }
  }, [allConfig]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const about = allConfig?.about || {};
  const site = allConfig?.identity || { siteName: 'Elecborn' };

  return (
     <div className="min-h-screen bg-bleu">
         <nav className="bg-bleu border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-3">
              <img src="/logo.png" alt="Elecborn Logo" className="h-10" />
            </Link>
            
            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <MobileMenu />
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-gray-700 hover:text-primary-600 font-medium transition-colors">
                Accueil
              </Link>
              <a href="/#services" className="text-gray-700 hover:text-primary-600 font-medium transition-colors">
                Services
              </a>
              <Link to="/simulateur" className="text-gray-700 hover:text-primary-600 font-medium transition-colors">
                Simulateur
              </Link>
              <Link to="/a-propos" className="text-gray-700 hover:text-primary-600 font-medium transition-colors">
                À propos
              </Link>
              <Link to="/contact" className="text-gray-700 hover:text-primary-600 font-medium transition-colors">
                Contact
              </Link>
              <Link to="/blog" className="text-gray-700 hover:text-primary-600 font-medium transition-colors">
                Blog
              </Link>
              <Link to="/login" className="flex items-center space-x-2 text-gray-700 hover:text-primary-600 font-medium transition-colors">
                <img
                  src="https://images.pexels.com/photos/614810/pexels-photo-614810.jpeg?auto=compress&cs=tinysrgb&w=32&h=32&dpr=2"
                  alt="CRM"
                  className="w-6 h-6 rounded-full object-cover border border-gray-200"
                />
                <span>Accès CRM</span>
              </Link>
            </div>
            
            {/* Desktop Button */}
            <div className="hidden md:block">
              <Link to="/devis">
                <Button className="bg-[#3295a2] hover:bg-[#267681] text-white">
                  Demander un devis
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>
 {/* Section Services */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Contenu texte */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Services
                </h2>
                <h3 className="text-xl font-semibold text-[#3295a2] mb-6">
                  Solutions IRVE
                </h3>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Notre proposition de valeur, chez Indelec Mobility, est de vous conseiller et de vous accompagner sur l'ensemble de la problématique de la recharge des véhicules électriques.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Et plus encore : nous vous proposons, en nous appuyant sur système de supervision ouvert et reconnu à l'échelle européenne, de gérer votre parc de bornes et vos groupes d'utilisateurs.
                </p>
              </div>

              {/* Section Audit Technique */}
              <div className="bg-gray-50 rounded-lg p-6 border-l-4 border-[#3295a2]">
                <div className="flex items-center mb-4">
                  <Target className="w-6 h-6 text-[#3295a2] mr-3" />
                  <h4 className="text-xl font-semibold text-gray-900">
                    Audit Technique
                  </h4>
                </div>
                <p className="text-gray-600">
                  Lorsque vous êtes confronté au besoin de solution de recharge pour véhicules électriques dans votre entreprise, nous déplaçons un chargé d'affaire sur votre site, et collectons toutes les informations nécessaires à l'étude de votre projet.
                </p>
              </div>

              {/* Call to Action */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button className="bg-[#3295a2] hover:bg-[#267681] text-white px-8 py-3">
                    Demander un audit
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button variant="outline" className="border-[#3295a2] text-[#3295a2] hover:bg-[#3295a2] hover:text-white px-8 py-3">
                    Voir tous nos services
                  </Button>
                </Link>
              </div>
            </div>

            {/* Image */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.pexels.com/photos/4480518/pexels-photo-4480518.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
                  alt="Solutions IRVE - Bornes de recharge véhicules électriques"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              </div>
              
              {/* Badge overlay */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-6 max-w-xs">
                <div className="flex items-center mb-2">
                  <CheckCircle2 className="w-5 h-5 text-green-500 mr-2" />
                  <span className="font-semibold text-gray-900">Expert certifié IRVE</span>
                </div>
                <p className="text-sm text-gray-600">
                  Solutions conformes aux normes européennes
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
        {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Logo & Description */}
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center mb-4">
                <img src="/logo.png" alt="Elecborn" className="h-12" />
              </div>
              <p className="text-gray-400 mb-6 max-w-md">
                Spécialiste des installations de bornes de recharge pour véhicules électriques. Faites confiance à notre expertise pour tous vos projets.
              </p>
              <div className="space-y-2">
                <div className="flex items-center text-gray-400">
                  <MapPin className="w-4 h-4 mr-2" />
                  <span>1 allée Pierre Puget 94000 Creteil.</span>
                </div>
                <div className="flex items-center text-gray-400">
                  <Phone className="w-4 h-4 mr-2" />
                  <a href="tel:0786115670">07 86 11 56 70</a>
                </div>
                <div className="flex items-center text-gray-400">
                  <Mail className="w-4 h-4 mr-2" />
                  <span>elecborn@gmail.com</span>
                </div>
              </div>
            </div>

            {/* Services */}
            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/services" className="hover:text-white transition-colors">Installation de bornes</Link></li>
                <li><Link to="/services" className="hover:text-white transition-colors">Maintenance préventive</Link></li>
                <li><Link to="/services" className="hover:text-white transition-colors">Dépannage d'urgence</Link></li>
                <li><Link to="/services" className="hover:text-white transition-colors">Audit et conseil</Link></li>
              </ul>
            </div>

            {/* Liens légaux */}
            <div>
              <h4 className="font-semibold mb-4">Informations</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/mentions-legales" className="hover:text-white transition-colors">Mentions légales</Link></li>
                <li><Link to="/cgv" className="hover:text-white transition-colors">CGV</Link></li>
                <li><Link to="/cookies" className="hover:text-white transition-colors">Cookies</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © 2024 Elecborn. Tous droits réservés.
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                Politique de confidentialité
              </a>
              <Link to="/login" className="text-[#3295a2] hover:text-[#267681] transition-colors">
                Accès CRM
              </Link>
            </div>
          </div>
        </div>
      </footer>

       </div>
  );
};

export default AproposPage;